/**
 * Mundalo - GA4 (gtag.js) wrapper.
 *
 * `gtag` is injected by /public/index.html. This module exposes typed
 * convenience functions for every event the product cares about so we can
 * keep the call-sites readable and the event-naming consistent.
 *
 * Every call is no-op safe when window.gtag is missing (e.g. SSR, ad-blockers).
 */

const safe = (...args) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag(...args);
    } catch {
      /* ignore */
    }
  }
};

export const trackPageview = (path) => {
  safe('event', 'page_view', { page_path: path });
};

export const trackEvent = (name, params = {}) => {
  safe('event', name, params);
};

// ===== Named convenience wrappers =====

export const trackSignupStarted = () =>
  trackEvent('signup_started');

export const trackSignupCompleted = ({ destination_city, move_type } = {}) =>
  trackEvent('signup_completed', { destination_city, move_type });

export const trackBlueprintUnlocked = ({ tier } = {}) =>
  trackEvent('blueprint_unlocked', { tier });

export const trackUpgradeWallShown = ({ feature, tier } = {}) =>
  trackEvent('upgrade_wall_shown', { feature, current_tier: tier });

export const trackUpgradeWallConverted = ({ feature, target_tier } = {}) =>
  trackEvent('upgrade_wall_converted', { feature, target_tier });

export const trackArticleRead = ({ slug, category } = {}) =>
  trackEvent('article_read', { slug, category });

// Fires when a reader clicks one of the "Keep reading" cards at the
// bottom of a Relo News article. `category` is the category of the
// destination card so we can see which adjacencies actually convert.
export const trackRelatedClick = ({ fromSlug, toSlug, category } = {}) =>
  trackEvent('related_article_click', {
    from_slug: fromSlug,
    to_slug: toSlug,
    category,
  });

export const trackCityGuideViewed = ({ city_slug } = {}) =>
  trackEvent('city_guide_viewed', { city_slug });

export const trackCommunityPostCreated = ({ board } = {}) =>
  trackEvent('community_post_created', { board });

export const trackDiscountClicked = ({ partner } = {}) => {
  trackEvent('discount_clicked', { partner });
  // Mirror server-side so admin can roll up clicks per partner.
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const url = process.env.REACT_APP_BACKEND_URL || '';
    try {
      fetch(`${url}/api/discounts/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ partner_id: partner }),
        keepalive: true,
      });
    } catch {
      /* ignore */
    }
  }
};

export const trackTrialExpired = () =>
  trackEvent('trial_expired');

// ===== iter 118 - GA4 conversion-funnel events =====
// 1. `purchase` - fires once per Stripe session on the post-checkout
//    confirmation page. Guarded by sessionStorage so a refresh of the
//    success page never double-counts revenue. GA4 already de-dupes by
//    transaction_id, but the local guard is a belt-and-braces.
export const trackPurchase = ({ plan_name, value, currency = 'USD', transaction_id } = {}) => {
  if (typeof window !== 'undefined' && transaction_id) {
    const key = `ga4_purchase_fired:${transaction_id}`;
    try {
      if (window.sessionStorage && window.sessionStorage.getItem(key)) return;
      if (window.sessionStorage) window.sessionStorage.setItem(key, '1');
    } catch { /* sessionStorage blocked - fire anyway */ }
  }
  trackEvent('purchase', {
    plan_name,
    value,
    currency,
    ...(transaction_id ? { transaction_id } : {}),
  });
};

// 2. `sign_up` - fires once when a new user lands on the dashboard for the
//    first time. Detected via `?welcome=trial` URL param (set by the
//    free-trial signup redirect in pages/StartFreeTrial.js).
export const trackSignUp = ({ method = 'email' } = {}) =>
  trackEvent('sign_up', { method });

// 3. `view_blueprint` - fires on Blueprint product page mount.
export const trackViewBlueprint = () =>
  trackEvent('view_blueprint');

// 4. `view_pricing` - fires when the Pricing section becomes visible
//    (homepage IntersectionObserver) or when the per-tier checkout page
//    loads. Both paths are legitimate "user is looking at prices" signals.
export const trackViewPricing = () =>
  trackEvent('view_pricing');

// 5. `begin_checkout` - fires the instant before window.location is
//    handed off to Stripe Checkout. Carries the same plan_name + value
//    contract as `purchase` so funnel conversion rate is computable
//    directly in GA4 without joins.
export const trackBeginCheckout = ({ plan_name, value, currency = 'USD' } = {}) =>
  trackEvent('begin_checkout', { plan_name, value, currency });


// 6. `nav_cta_click` - iter 126 - fires the moment a visitor clicks the
//    persistent header CTA. Mirrored to `sessionStorage` so the signup
//    form can attribute the resulting user to a `signup_source`. This
//    powers the "Conversion Path Comparison" card on the admin dashboard
//    without needing GA4 Data API service-account creds.
//
//    source ∈ {'homepage_blueprint_cta', 'other_page_start_free_cta'}.
export const trackNavCtaClick = ({ source } = {}) => {
  if (source && typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem('mundalo_signup_source', source);
    } catch (e) {
      /* sessionStorage can be disabled in private windows - non-fatal */
    }
  }
  trackEvent('nav_cta_click', { source });
};


// ===== iter 136 - Free SEO/conversion tools =====
//
// One-shot fire per session per event. Refreshing the results page after
// completing a quiz must not double-count. We use the same sessionStorage
// guard pattern as `trackPurchase` above. `dedupKey` is the cache key -
// pass a stable string (event name plus a salient parameter) so distinct
// tool runs in the same session still register independently.
const fireOnce = (dedupKey, name, params) => {
  if (typeof window !== 'undefined') {
    try {
      if (window.sessionStorage && window.sessionStorage.getItem(dedupKey)) return;
      if (window.sessionStorage) window.sessionStorage.setItem(dedupKey, '1');
    } catch { /* sessionStorage blocked - fire anyway */ }
  }
  trackEvent(name, params);
};

export const trackMoveScoreCompleted = ({ score, score_label, q2_city, q6_concern } = {}) =>
  fireOnce(
    `ga4_move_score_completed:${score}:${q2_city || ''}:${q6_concern || ''}`,
    'move_score_completed',
    { score, score_label, q2_city, q6_concern },
  );

export const trackCostCalcCompleted = ({ city, lifestyle, monthly_total, household } = {}) =>
  fireOnce(
    `ga4_cost_calc_completed:${city}:${lifestyle}:${household}`,
    'cost_calc_completed',
    { city, lifestyle, monthly_total, household },
  );

export const trackVisaFinderCompleted = ({ primary_visa, q1_situation } = {}) =>
  fireOnce(
    `ga4_visa_finder_completed:${primary_visa}:${q1_situation}`,
    'visa_finder_completed',
    { primary_visa, q1_situation },
  );

export const trackScoreEmailCaptured = ({ score } = {}) =>
  fireOnce(
    `ga4_score_email_captured:${score}`,
    'score_email_captured',
    { score },
  );

// iter 139 - Cost Calculator email capture conversion event.
export const trackCalculatorEmailCaptured = ({ city, monthly_total } = {}) =>
  fireOnce(
    `ga4_calculator_email_captured:${city}:${monthly_total}`,
    'calculator_email_captured',
    { city, monthly_total },
  );

// iter 139 - Visa Finder email capture conversion event.
export const trackVisaEmailCaptured = ({ primary_visa } = {}) =>
  fireOnce(
    `ga4_visa_email_captured:${primary_visa}`,
    'visa_email_captured',
    { primary_visa },
  );

// iter 137b - Homepage tool-strip chip taps. No dedup: every tap is a
// meaningful intent signal and we want the raw rate for the chip-click →
// tool-completion → blueprint-purchase funnel.
//   tool  ∈ {'move_score', 'cost_calculator', 'visa_finder'}
//   value = the human-readable chip label (e.g. 'Just me', 'Mexico City')
export const trackToolChipClicked = ({ tool, value } = {}) =>
  trackEvent('tool_chip_clicked', { tool, value });


// iter 144q - Start Quiz (/start) paid-social funnel events.
export const trackQuizStarted = () =>
  trackEvent('quiz_started', {});

// iter 144u - Per-screen view event for drop-off attribution.
// Fires on every screen advance, including buffer screens (5, 10),
// the email gate (6), the loading screen (13), and the result (14).
// `screen` is the screen number 1-14.
//
// iter 187.n - `screen` is cast to Number at the wrapper boundary so
// gtag transmits it as `epn.screen` (numeric) instead of `ep.screen`
// (text). Without the cast, GA4 Explorations can't range-filter
// (`screen >= 6`) - only exact-string equality would work.
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export const trackQuizScreenView = ({ screen } = {}) =>
  trackEvent('quiz_screen_view', { screen: toNum(screen) });

// iter 144w - Cost of Living Calculator V2 events.
export const trackCalculatorV2Started = ({ funnel_session_id } = {}) =>
  trackEvent('calculator_v2_started', { funnel_session_id });

export const trackCalculatorV2EmailCaptured = ({ persona, age } = {}) =>
  trackEvent('calculator_v2_email_captured', { persona, age });

// iter 187.c - Progressive-capture funnel events. Both share the
// same funnel_session_id so GA4 Explore can reconstruct the sequence.
// iter 187.n - Numeric fields (household, budget, age) cast via
// `toNum` so gtag transmits them as `epn.*` (range-filterable in
// GA4 Explore) instead of `ep.*` (text equality only).
export const trackCalculatorCheckpointReached = ({ funnel_session_id, persona, household, city_1 } = {}) =>
  trackEvent('calculator_checkpoint_reached', {
    funnel_session_id, persona, household: toNum(household), city_1,
  });

export const trackCalculatorCheckpointCaptured = ({ funnel_session_id, persona, age } = {}) =>
  trackEvent('calculator_checkpoint_captured', {
    funnel_session_id, persona, age: toNum(age),
  });

export const trackCalculatorV2Completed = ({ funnel_session_id, persona, budget, city_1, priority, captured_at_checkpoint } = {}) =>
  trackEvent('calculator_v2_completed', {
    funnel_session_id, persona, budget: toNum(budget), city_1, priority, captured_at_checkpoint,
  });

export const trackCalculatorV2CtaClicked = ({ plan } = {}) =>
  trackEvent('calculator_v2_cta_clicked', { plan });

export const trackQuizEmailCaptured = ({ source = 'start_quiz' } = {}) =>
  trackEvent('quiz_email_captured', { source });

export const trackQuizCompleted = ({ city, situation } = {}) =>
  trackEvent('quiz_completed', { city: city || null, situation: situation || null });

export const trackQuizCtaClicked = ({ plan } = {}) =>
  trackEvent('quiz_cta_clicked', { plan });

// iter 144r - Exit-intent overlay on the /start quiz Screen 14.
// One event per overlay lifecycle. We do NOT fireOnce-guard `shown`
// because legitimately re-triggering the overlay (e.g. user dismisses,
// idles again, mouseleaves again) is a meaningful signal.
//   trigger ∈ {'mouseleave', 'idle_90s', 'manual'}
export const trackQuizExitIntentShown = ({ trigger } = {}) =>
  trackEvent('quiz_exit_intent_shown', { trigger });

export const trackQuizExitIntentConverted = ({ trigger } = {}) =>
  trackEvent('quiz_exit_intent_converted', { trigger });

export const trackQuizExitIntentDismissed = ({ trigger } = {}) =>
  trackEvent('quiz_exit_intent_dismissed', { trigger });

// iter 154 - Fired once on /quiz-claim after a successful account
// activation. Captures final tier, whether the user upgraded from
// Blueprint Only, and total $ paid (in cents). Drives the upsell A/B
// conversion view in GA4.
export const trackQuizFunnelCompleted = ({ tier, upgraded_from_blueprint_only, total_paid_cents } = {}) =>
  trackEvent('quiz_funnel_completed', {
    tier,
    upgraded_from_blueprint_only: !!upgraded_from_blueprint_only,
    total_paid_cents,
  });

